import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, RefreshCw, Search } from "lucide-react";
import WhiteTitledCard from "../../../../components/cards/WhiteTitledCard";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import {
  fetchPaymentOptions,
  togglePaymentOptionThunk,
  PaymentOption,
} from "../../../../redux/features/paymentOptionsSlice";
import { PaymentOptionStatus } from "../../../../apis/paymentOptions";
import PaymentMethodsTable from "./PaymentMethodsTable";
import ConfirmDisableModal from "./modals/ConfirmDisableModal";
import { prettifyPaymentOptionName } from "./utils";

type StatusFilter = "ALL" | "ACTIVE" | "INACTIVE";

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "ALL" },
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const PaymentMethods: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { items, loading, toggling } = useAppSelector(
    (state) => state.paymentOptions
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [pendingDisable, setPendingDisable] = useState<PaymentOption | null>(
    null
  );

  useEffect(() => {
    dispatch(fetchPaymentOptions());
  }, [dispatch]);

  const performToggle = async (
    item: PaymentOption,
    nextStatus: PaymentOptionStatus
  ) => {
    try {
      await dispatch(
        togglePaymentOptionThunk({
          id: item.id,
          status: nextStatus,
          prevStatus: String(item.status),
        })
      ).unwrap();
      toast.success(
        `${prettifyPaymentOptionName(item.name)} ${
          nextStatus === "ACTIVE" ? "enabled" : "disabled"
        }`
      );
    } catch {
      // Error toast + state rollback are handled inside the thunk.
    }
  };

  const handleToggleRequest = (item: PaymentOption) => {
    if (toggling[item.id]) return;
    if (item.status === "ACTIVE") {
      // Disabling is destructive — gate behind a confirmation modal.
      setPendingDisable(item);
      return;
    }
    performToggle(item, "ACTIVE");
  };

  const confirmDisable = async () => {
    if (!pendingDisable) return;
    const item = pendingDisable;
    setPendingDisable(null);
    await performToggle(item, "INACTIVE");
  };

  const total = items.length;
  const activeCount = items.filter((i) => i.status === "ACTIVE").length;
  const inactiveCount = total - activeCount;

  return (
    <div className="p-6 flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-bold text-2xl">Payment Methods</h1>
            <p className="text-sm text-gray-500">
              Enable or disable the payment options shown to buyers at
              checkout.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => dispatch(fetchPaymentOptions())}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-primary hover:bg-lightPurple px-3 py-2 rounded-md disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryTile label="Total" value={total} />
        <SummaryTile label="Active" value={activeCount} accent="green" />
        <SummaryTile label="Inactive" value={inactiveCount} accent="gray" />
      </div>

      <WhiteTitledCard>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="relative w-full sm:w-[280px]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or code"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            {FILTERS.map((f) => {
              const isSelected = statusFilter === f.value;
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setStatusFilter(f.value)}
                  className={`text-xs font-medium px-3 py-2 rounded-full border transition-colors ${
                    isSelected
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        <PaymentMethodsTable
          search={search}
          statusFilter={statusFilter}
          onToggle={handleToggleRequest}
        />
      </WhiteTitledCard>

      <ConfirmDisableModal
        isOpen={!!pendingDisable}
        methodName={
          pendingDisable ? prettifyPaymentOptionName(pendingDisable.name) : ""
        }
        loading={pendingDisable ? !!toggling[pendingDisable.id] : false}
        onClose={() => setPendingDisable(null)}
        onConfirm={confirmDisable}
      />
    </div>
  );
};

const SummaryTile: React.FC<{
  label: string;
  value: number;
  accent?: "green" | "gray";
}> = ({ label, value, accent }) => {
  const valueColor =
    accent === "green"
      ? "text-green-600"
      : accent === "gray"
      ? "text-gray-500"
      : "text-gray-800";
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-semibold mt-1 ${valueColor}`}>{value}</p>
    </div>
  );
};

export default PaymentMethods;
